import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Chip, Table, TableBody, TableCell, TableRow, Tooltip } from '@mui/material';
import { FC } from 'react';
import { SectionTitle } from 'src/components/SectionTitle';
import { Colors } from 'src/components/utils/Theme';
import { MangopayReadiness } from 'src/models/users';

const cellStyle = {
  fontFamily: 'aestetico-semibold',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '50px',
} as any;
const cellStyleValue = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '50px',
} as any;

/** Green check when true / red close when false, matching the DeletionsSite flag style. */
const ReadinessFlag: FC<{ ok?: boolean; tooltip?: string }> = ({ ok, tooltip }) => {
  const icon = ok ? (
    <CheckIcon fontSize="small" style={{ color: Colors.SUCCESS }} />
  ) : (
    <CloseIcon fontSize="small" style={{ color: Colors.ERROR }} />
  );
  return (
    <Tooltip title={tooltip ?? (ok ? 'true' : 'false')}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
    </Tooltip>
  );
};

/**
 * Compact panel showing the Mangopay / SCA readiness ticks stored on
 * `userData.mangopayReadiness`. All flags render as check (true) / close (false)
 * icons; the overall `isReady` is surfaced additionally as a summary Chip.
 */
export const MangopayReadinessComponent: FC<{ readiness?: MangopayReadiness }> = ({ readiness }) => {
  return (
    <>
      <SectionTitle>Mangopay / SCA status</SectionTitle>
      {!readiness ? (
        <Alert severity="info" sx={{ m: 1 }}>
          No Mangopay / SCA readiness data available for this user.
        </Alert>
      ) : (
        <Table>
          <TableBody>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>Overall status</TableCell>
              <TableCell style={cellStyleValue}>
                <Chip
                  size="small"
                  label={readiness.isReady ? 'Ready' : 'Not ready'}
                  color={readiness.isReady ? 'success' : 'default'}
                />
              </TableCell>
            </TableRow>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>Account created</TableCell>
              <TableCell style={cellStyleValue}>
                <ReadinessFlag ok={readiness.accountCreated} />
              </TableCell>
            </TableRow>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>KYC verified</TableCell>
              <TableCell style={cellStyleValue}>
                <ReadinessFlag ok={readiness.kycVerified} />
              </TableCell>
            </TableRow>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>SCA enrolled</TableCell>
              <TableCell style={cellStyleValue}>
                <ReadinessFlag ok={readiness.scaEnrolled} />
              </TableCell>
            </TableRow>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>Consent: transfer</TableCell>
              <TableCell style={cellStyleValue}>
                <ReadinessFlag ok={readiness.consent?.transfer} />
              </TableCell>
            </TableRow>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>Consent: recipient registration</TableCell>
              <TableCell style={cellStyleValue}>
                <ReadinessFlag ok={readiness.consent?.recipientRegistration} />
              </TableCell>
            </TableRow>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>Consent: contact information update</TableCell>
              <TableCell style={cellStyleValue}>
                <ReadinessFlag ok={readiness.consent?.contactInformationUpdate} />
              </TableCell>
            </TableRow>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>Consent: view account information</TableCell>
              <TableCell style={cellStyleValue}>
                <ReadinessFlag ok={readiness.consent?.viewAccountInformation} />
              </TableCell>
            </TableRow>
            <TableRow hover={true}>
              <TableCell style={cellStyle}>Outflows blocked</TableCell>
              <TableCell style={cellStyleValue}>
                <ReadinessFlag ok={readiness.outflowsBlocked} tooltip={readiness.outflowsBlocked ? 'Outflows are blocked' : 'Outflows are not blocked'} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    </>
  );
};
